import React, { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import type { StylesConfig } from 'react-select';
import { useHelpCategoriesQuery } from '@api/services/lookup/hooks';
import MultiSelectInput from '@components/multi-select/multiSelectInput';
import { normalizeHelpCategoryOptions } from './helpers';
import { FormValues, HelpCategoryOption } from './types';

const requestAssistanceMultiSelectStyles: StylesConfig<HelpCategoryOption, true> = {
  container: (base) => ({
    ...base,
    width: '100%',
    maxWidth: '100%',
  }),
  control: (base) => ({
    ...base,
    width: '100%',
    minWidth: '20rem',
    maxWidth: '100%',
  }),
  menu: (base) => ({
    ...base,
    width: '100%',
    minWidth: '20rem',
    maxWidth: '100%',
  }),
  menuList: (base) => ({
    ...base,
    maxWidth: '100%',
  }),
  option: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
  }),
  multiValue: (base) => ({
    ...base,
    maxWidth: '100%',
  }),
  multiValueLabel: (base) => ({
    ...base,
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
  }),
};

const Questions: React.FC = () => {
  const { clearErrors, setValue } = useFormContext<FormValues>();

  const {
    data: helpCategories = [],
    isError,
    isLoading,
  } = useHelpCategoriesQuery({
    active: true,
  });

  const helpCategoryOptions = useMemo<HelpCategoryOption[]>(() => {
    return helpCategories.map((category) => ({
      value: category.category,
      label: category.description,
    }));
  }, [helpCategories]);

  const handleHelpCategoryChange = (selectedOptions: readonly HelpCategoryOption[] | null = []) => {
    const normalizedOptions = normalizeHelpCategoryOptions(selectedOptions, helpCategoryOptions);

    setValue('helpCategories', normalizedOptions, {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    });

    setValue('reason', '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });

    clearErrors('reason');
  };

  return (
    <div className='request-assistance-category-select margin-bottom-3'>
      <MultiSelectInput
        multi
        name='helpCategories'
        label='What type of regulator assistance do you need?'
        options={helpCategoryOptions}
        handleChange={handleHelpCategoryChange}
        required
        styles={requestAssistanceMultiSelectStyles}
        isLoading={isLoading}
        readOnly={isLoading || isError}
        fileDownload={undefined}
        filterKeys={undefined}
        formatOptionLabel={undefined}
        handleBlur={undefined}
        hint={undefined}
        isClearable={undefined}
        menuPlacement={undefined}
        menuPosition={undefined}
        subtext={undefined}
        tooltip={undefined}
        tooltipClickable={undefined}
      />
    </div>
  );
};

export default Questions;
